import { SessionEvent } from '@/models/Metrics/SessionEvent';
import { v4 as uuidv4 } from 'uuid';
import useAxios from '@/composables/useAxios';
import { reactive, Reactive, readonly } from 'vue';
import getCreatedAt from '@/utils/formatSessionEventDate';

const LOCAL_STORAGE_SESSION_EVENTS_KEY_PREFIX = 'session_events_';

/**
 * A reactive collection of events indexed by session ID.
 * Represents all the cached events ready to be sent with the API.
 */
const eventsBuffer: Reactive<Record<string, StoredSessionEvent[]>> = reactive({});

/**
 * Tail of the flush queue: every flush chains onto it so that only one upload runs at a time.
 */
let flushQueue: Promise<void> = Promise.resolve();

/**
 * Flushes queued but not started yet, by session ID. Requesting a flush for a session that is
 * already waiting returns the same promise instead of queueing a duplicate.
 */
const waitingFlushes = new Map<string, Promise<void>>();

/**
 * Represents a stored session event in local storage that extends the base SessionEvent interface.
 * This interface includes an additional property `_tempId` to hold a temporary identifier for the session event.
 *
 * @interface StoredSessionEvent
 * @extends SessionEvent
 *
 * @property {string} _tempId - A temporary identifier associated with the session event.
 */
interface StoredSessionEvent extends SessionEvent {
    _tempId: string;
}

interface SessionEventApiRequestBody {
    student_id?: number;
    scope: string;
    task_id: string | number | null;
    step_id: string | null;
    type: string;
    ref: string | null;
    value: string | null;
    created_at: string;
}

export default function useSessionEvents() {
    const axios = useAxios();

    /**
     * Generates a session key by appending the session ID to a predefined prefix.
     *
     * @param {string} sessionId - The unique identifier for the session.
     * @return {string} The generated session key.
     */
    function getSessionKey(sessionId: string): string {
        return `${LOCAL_STORAGE_SESSION_EVENTS_KEY_PREFIX}${sessionId}`;
    }

    /**
     * Retrieves the events stored in the local storage for a specific session.
     *
     * @param {string} sessionId - The unique identifier of the session for which events should be retrieved.
     * @return {StoredSessionEvent[]} An array of events stored in the local storage for the provided session ID.
     * Returns an empty array if no events are found or if the data is not available.
     */
    function getEventsFromLocalStorage(sessionId: string): StoredSessionEvent[] {
        const storedEvents = localStorage.getItem(getSessionKey(sessionId));
        if (!storedEvents) {
            return [];
        }
        try {
            const parsed = JSON.parse(storedEvents);
            if (!Array.isArray(parsed)) {
                throw new Error('Stored session events are not an array');
            }
            return parsed;
        } catch (error) {
            console.error('Unreadable stored session events, ignored:', sessionId, error);
            return [];
        }
    }

    /**
     * Retrieves all session IDs stored in local storage by filtering keys with the session events prefix.
     *
     * @return {string[]} An array of session IDs from local storage.
     */
    function getAllSessionIdsFromLocalStorage(): string[] {
        return Object.keys(localStorage)
            .filter((key) => key.startsWith(LOCAL_STORAGE_SESSION_EVENTS_KEY_PREFIX))
            .map((key) => key.replace(LOCAL_STORAGE_SESSION_EVENTS_KEY_PREFIX, ''));
    }

    /**
     * Retrieves all session IDs stored in the events buffer.
     *
     * @return {string[]} An array of session IDs from the events buffer.
     */
    /* function getAllSessionIds(): string[] {
        return Object.keys(eventsBuffer);
    } */

    /**
     * Stores a session event in a buffer by appending it to the list of events
     * for a given session ID and persisting the updated list in localStorage.
     *
     * @param {string} sessionId - The unique identifier for the session.
     * @param {SessionEvent} event - The event object associated with the session,
     *                                which contains details of the session event.
     * @return {void} - This function does not return any value.
     */
    function storeSessionEventInBuffer(sessionId: string, event: SessionEvent): void {
        if (process.env.VUE_APP_DEBUG === 'true') {
            console.log('Store session event', event);
        }

        // Add the new event with a temporary ID to the list
        const storedEvent: StoredSessionEvent = {
            ...event,
            _tempId: uuidv4(),
        };

        // Retrieve existing events from localStorage for this session
        const events = getEventsFromLocalStorage(sessionId);
        events.push(storedEvent);
        // Store the updated list in localStorage with a session-specific key
        localStorage.setItem(getSessionKey(sessionId), JSON.stringify(events));

        if (!eventsBuffer[sessionId]) {
            eventsBuffer[sessionId] = [];
        }

        eventsBuffer[sessionId].push(storedEvent);
    }

    /**
     * Clears all events from the buffer. If a specific session ID is provided, only events
     * related to that session will be cleared. Otherwise, clears events for all sessions.
     *
     * @param {string} [sessionId] - Optional session ID. If provided, only events related
     * to this session will be removed.
     * @return {void} This method does not return any value.
     */
    /* function clearAllEventsFromBuffer(sessionId?: string): void {
        if (sessionId) {
            // Remove events for a specific session
            localStorage.removeItem(getSessionKey(sessionId));
            delete eventsBuffer[sessionId];
        } else {
            // Remove all events from all sessions
            getAllSessionIds().forEach((id) => {
                localStorage.removeItem(getSessionKey(id));
                delete eventsBuffer[id];
            });
        }
    } */

    /**
     * Removes specific events from the buffer associated with the provided session ID.
     *
     * @param {string} sessionId - The ID of the session whose buffer events need to be removed.
     * @param {StoredSessionEvent[]} eventsToRemove - An array of events to be removed from the session buffer.
     * @return {void} This function does not return a value.
     */
    function removeEventsFromBuffer(sessionId: string, eventsToRemove: StoredSessionEvent[]): void {
        const eventsToRemoveIds = eventsToRemove.map((event) => event._tempId);
        const sessionEvents = eventsBuffer[sessionId];

        if (!sessionEvents) {
            return;
        }

        for (let i = sessionEvents.length - 1; i >= 0; i--) {
            if (eventsToRemoveIds.includes(sessionEvents[i]._tempId)) {
                sessionEvents.splice(i, 1);
            }
        }

        if (sessionEvents.length === 0) {
            localStorage.removeItem(getSessionKey(sessionId));
        } else {
            localStorage.setItem(getSessionKey(sessionId), JSON.stringify(sessionEvents));
        }
    }

    /**
     * Sends a batch of events to the server for a specific session.
     *
     * @param sessionId
     * @param {StoredSessionEvent[]} events - An array of events to be sent. Each event must conform to the StoredSessionEvent format.
     * @return {Promise<void>} A promise that resolves when the events are successfully sent, or throws an error if the operation fails.
     */
    async function sendEvents(sessionId: string, events: StoredSessionEvent[]): Promise<void> {
        const formattedEvents = events.map((event) => {
            const sessionEvent: SessionEventApiRequestBody = {
                scope: event.scope,
                task_id: event.taskId ?? null,
                step_id: event.stepId ?? null,
                type: event.type,
                ref: event.ref ?? null,
                value: event.value ? JSON.stringify(event.value) : null,
                created_at: getCreatedAt(event.createdAt),
            };

            if (typeof event.studentId === 'number') {
                sessionEvent.student_id = event.studentId;
            } else {
                console.warn('Sending event without student ID:', event);
            }

            return sessionEvent;
        });

        try {
            await axios.post(`sessions/${sessionId}/events`, {
                events: formattedEvents,
            });
        } catch (error) {
            console.error('Failed to send session events:', error);
            throw error;
        }
    }

    /**
     * Uploads the events currently buffered for a session. Never rejects.
     *
     * @param {string} sessionId - The unique identifier of the session whose events need to be sent.
     * @return {Promise<void>} A promise that resolves once the upload attempt is complete.
     */
    async function uploadBufferedEvents(sessionId: string): Promise<void> {
        try {
            // Create a copy of the events in the buffer to avoid having modifications in the array during the flush
            const events = [...(eventsBuffer[sessionId] || [])];

            if (events.length > 0) {
                await sendEvents(sessionId, events);
                // Remove the events sent from the buffer if the operation is successful
                removeEventsFromBuffer(sessionId, events);
            }
        } catch (error) {
            console.error('Impossible to flush the user metrics to the server:', error);
        }
    }

    /**
     * Flushes session events for the specified session by sending them to the server.
     * Successfully sent events are removed from the local storage buffer. Flushes are serialized:
     * this one runs after any flush already in progress, and a flush already waiting for the same
     * session is reused.
     *
     * @param {string} sessionId - The unique identifier of the session whose events need to be flushed.
     * @return {Promise<void>} A promise that resolves once the flushing operation is complete. Never rejects.
     */
    function flushSessionEvents(sessionId: string): Promise<void> {
        const waiting = waitingFlushes.get(sessionId);
        if (waiting) {
            return waiting;
        }

        const run = flushQueue.then(() => {
            waitingFlushes.delete(sessionId);
            return uploadBufferedEvents(sessionId);
        });
        waitingFlushes.set(sessionId, run);
        flushQueue = run;

        return run;
    }

    /**
     * Restores session events from local storage into the events buffer for a specific session.
     *
     * @param {string} sessionId - The unique identifier of the session whose events should be restored.
     * @return {void} This function does not return any value.
     */
    function restoreEventsFromLocalStorage(sessionId: string): void {
        eventsBuffer[sessionId] = getEventsFromLocalStorage(sessionId).map((event: StoredSessionEvent) => {
            if (typeof event.createdAt === 'string') {
                // eslint-disable-next-line no-param-reassign
                event.createdAt = new Date(event.createdAt);
            }
            return event;
        });
    }

    async function flushAllSessionEvents(): Promise<void> {
        const sessionIds = getAllSessionIdsFromLocalStorage();
        await Promise.all(
            sessionIds.map((sessionId) => {
                // Versions before #842 rewrote a flushed key as "[]" instead of removing it: drop
                // those. An unreadable value is kept — nothing should delete what it cannot read.
                if (localStorage.getItem(getSessionKey(sessionId)) === '[]') {
                    localStorage.removeItem(getSessionKey(sessionId));
                    return Promise.resolve();
                }
                restoreEventsFromLocalStorage(sessionId);
                return flushSessionEvents(sessionId);
            })
        );
    }

    return {
        eventsBuffer: readonly(eventsBuffer),
        storeSessionEventInBuffer,
        flushSessionEvents,
        flushAllSessionEvents,
        restoreEventsFromLocalStorage,
    };
}
